import { Globe, Scale, ChevronDown, Check, CircleDollarSign } from 'lucide-react';

import { CurrencySwitcher } from '@/components/currency/CurrencySwitcher.tsx';
import { Link } from '@/components/design-system';
import {
  COLOR_SCALE,
  FONT_WEIGHTS,
  GAP_SCALE,
  PADDING_X,
  PADDING_Y,
  PADDING_TOP,
  PADDING_BOTTOM,
  RADIUS_SCALE,
  SIZE_SCALE,
  TYPE_SCALE,
  MARGIN_TOP,
  MARGIN_BOTTOM,
  TEXT_ALIGNMENT,
} from '@/components/design-system/tokens';
import { languages } from '@/components/layout/constants';
import { useCurrency } from '@/hooks/useCurrency';
import { createPageUrl } from '@/utils';

interface PreferencesSectionProps {
  currentLanguage: string;
  languageOpen: boolean;
  legalOpen: boolean;
  onLanguageToggle: () => void;
  onLegalToggle: () => void;
  onLanguageChange: (language: string) => void;
  scrollToTop: () => void;
  onClose: () => void;
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string;
}

export const PreferencesSection = ({
  currentLanguage,
  languageOpen,
  legalOpen,
  onLanguageToggle,
  onLegalToggle,
  onLanguageChange,
  scrollToTop,
  onClose,
  t,
}: PreferencesSectionProps) => {
  const { selectedCurrency } = useCurrency();

  return (
    <div className={`${PADDING_TOP['4']} ${MARGIN_TOP['4']}`}>
      <div className={`${PADDING_X['3']} ${MARGIN_BOTTOM['3']}`}>
        <p
          className={`text-xs ${FONT_WEIGHTS.semibold} ${COLOR_SCALE['icon-muted']} uppercase tracking-wider`}
        >
          {t('mobile_menu', 'preferences_heading', 'Preferences')}
        </p>
      </div>

      {/* Language */}
      <div
        className={`${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} shadow-sm overflow-hidden ${MARGIN_BOTTOM['2']}`}
      >
        <button
          onClick={onLanguageToggle}
          className={`w-full flex items-center justify-between ${PADDING_X['4']} ${PADDING_Y['4']} hover:${COLOR_SCALE['surface-muted']} transition-colors`}
        >
          <div className={`flex items-center ${GAP_SCALE['3']}`}>
            <div
              className={`${SIZE_SCALE.w9} ${SIZE_SCALE['9']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['blue-100-bg']} flex items-center justify-center`}
            >
              <Globe
                className={`${SIZE_SCALE['w4.5']} ${SIZE_SCALE['4.5']} ${COLOR_SCALE['blue-600']}`}
              />
            </div>
            <div className={`${TEXT_ALIGNMENT.left}`}>
              <p className={`text-sm ${FONT_WEIGHTS.normal} ${COLOR_SCALE.foreground}`}>
                {languages.find(l => l.code === currentLanguage)?.name ??
                  t('mobile_menu', 'language_default', 'Language')}
              </p>
              <p
                className={`${TYPE_SCALE.xs} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['admin-muted-text']}`}
              >
                {t('mobile_menu', 'change_language', 'Change language')}
              </p>
            </div>
          </div>
          <ChevronDown
            className={`${SIZE_SCALE.iconMd} ${COLOR_SCALE['icon-muted']} transition-transform duration-200 ${
              languageOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {languageOpen && (
          <div
            className={`border-t ${COLOR_SCALE['admin-border']} ${COLOR_SCALE['surface-muted']} ${PADDING_X['2']} ${PADDING_Y['2']} max-h-64 overflow-y-auto`}
          >
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => onLanguageChange(lang.code)}
                className={`w-full flex items-center justify-between ${PADDING_X['3']} ${PADDING_Y['2.5']} ${RADIUS_SCALE.lg} transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 focus-visible:ring-offset-0 border-b border-slate-200 last:border-b-0 ${
                  currentLanguage === lang.code
                    ? 'bg-blue-50 text-blue-700'
                    : `hover:${COLOR_SCALE.surface} ${COLOR_SCALE['gray-700']}`
                }`}
              >
                <div className={`flex items-center ${GAP_SCALE['3']}`}>
                  <span className={`${TYPE_SCALE.xl}`}>{lang.flag}</span>
                  <span className={`text-sm ${FONT_WEIGHTS.normal}`}>{lang.name}</span>
                </div>
                {currentLanguage === lang.code && (
                  <Check className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} ${COLOR_SCALE.primary}`} />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Currency */}
      <div
        className={`${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} shadow-sm overflow-hidden ${MARGIN_BOTTOM['2']}`}
      >
        <div
          className={`w-full flex items-center justify-between ${PADDING_X['4']} ${PADDING_Y['4']}`}
        >
          <div className={`flex items-center ${GAP_SCALE['3']}`}>
            <div
              className={`${SIZE_SCALE.w9} ${SIZE_SCALE['9']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['emerald-50-bg']} flex items-center justify-center`}
            >
              <CircleDollarSign
                className={`${SIZE_SCALE['w4.5']} ${SIZE_SCALE['4.5']} ${COLOR_SCALE['emerald-600']}`}
              />
            </div>
            <div className={`${TEXT_ALIGNMENT.left}`}>
              <p className={`text-sm ${FONT_WEIGHTS.normal} ${COLOR_SCALE.foreground}`}>
                {t('mobile_menu', 'currency_title', 'Price (in country)')}
              </p>
              <p className={`${TYPE_SCALE.xs} ${COLOR_SCALE['admin-muted-text']}`}>
                {t('mobile_menu', 'currency_subtitle', 'See costs in {{currency}}', {
                  currency: selectedCurrency,
                })}
              </p>
            </div>
          </div>
          <CurrencySwitcher
            hideLabel
            size="sm"
            variant="ghost"
            ariaLabel={t('currency', 'change_currency', 'Change currency')}
            className={`rounded-full ${PADDING_X['3']} ${PADDING_Y['1.5']} text-sm ${FONT_WEIGHTS.semibold} uppercase tracking-wide ${COLOR_SCALE['gray-700']} hover:text-blue-700 hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0`}
          />
        </div>
        <p
          className={`${PADDING_X['4']} ${PADDING_BOTTOM['4']} text-xs ${COLOR_SCALE['admin-muted-text']} leading-relaxed`}
        >
          {t(
            'mobile_menu',
            'currency_hint',
            'Adjust all treatment prices to match your preferred currency.'
          )}
        </p>
      </div>

      {/* Legal & Policies */}
      <div className={`${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} shadow-sm overflow-hidden`}>
        <button
          onClick={onLegalToggle}
          className={`w-full flex items-center justify-between ${PADDING_X['4']} ${PADDING_Y['4']} hover:${COLOR_SCALE['surface-muted']} transition-colors`}
        >
          <div className={`flex items-center ${GAP_SCALE['3']}`}>
            <div
              className={`${SIZE_SCALE.w9} ${SIZE_SCALE['9']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['surface-subtle']} flex items-center justify-center`}
            >
              <Scale
                className={`${SIZE_SCALE['w4.5']} ${SIZE_SCALE['4.5']} ${COLOR_SCALE['foreground-muted']}`}
              />
            </div>
            <div className={`${TEXT_ALIGNMENT.left}`}>
              <p className={`text-sm ${FONT_WEIGHTS.normal} ${COLOR_SCALE.foreground}`}>
                {t('navigation', 'menu_legal_policies', 'Legal & Policies')}
              </p>
              <p
                className={`${TYPE_SCALE.xs} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['admin-muted-text']}`}
              >
                {t('mobile_menu', 'legal_section_subtitle', 'Terms, privacy & more')}
              </p>
            </div>
          </div>
          <ChevronDown
            className={`${SIZE_SCALE.iconMd} ${COLOR_SCALE['icon-muted']} transition-transform duration-200 ${
              legalOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {legalOpen && (
          <div
            className={`border-t ${COLOR_SCALE['admin-border']} ${COLOR_SCALE['surface-muted']} ${PADDING_X['2']} ${PADDING_Y['2']}`}
          >
            <Link
              to={createPageUrl('TermsConditions')}
              onClick={() => {
                scrollToTop();
                onClose();
              }}
              className={`block ${PADDING_X['3']} ${PADDING_Y['2']}.5 text-sm font-medium ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all`}
            >
              {t('navigation', 'terms_conditions', 'Terms & Conditions')}
            </Link>
            <Link
              to={createPageUrl('PrivacyPolicy')}
              onClick={() => {
                scrollToTop();
                onClose();
              }}
              className={`block ${PADDING_X['3']} ${PADDING_Y['2']}.5 text-sm font-medium ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all`}
            >
              {t('navigation', 'privacy_policy', 'Privacy Policy')}
            </Link>
            <Link
              to={createPageUrl('CookiePolicy')}
              onClick={() => {
                scrollToTop();
                onClose();
              }}
              className={`block ${PADDING_X['3']} ${PADDING_Y['2']}.5 text-sm font-medium ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all`}
            >
              {t('navigation', 'cookie_policy', 'Cookie Policy')}
            </Link>
            <Link
              to={createPageUrl('MedicalDisclaimer')}
              onClick={() => {
                scrollToTop();
                onClose();
              }}
              className={`block ${PADDING_X['3']} ${PADDING_Y['2']}.5 text-sm font-medium ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.surface} hover:${COLOR_SCALE.foreground} ${RADIUS_SCALE.lg} transition-all`}
            >
              {t('navigation', 'medical_disclaimer', 'Medical Disclaimer')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
